import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

interface AuthGuardProps {
  children: React.ReactNode;
}

interface DecodedToken {
  exp: number;
  [key: string]: any;
}

// Wipes the session but keeps the user's light/dark preference — logging out
// shouldn't dump you back onto a light login screen if you'd switched to dark.
const clearSessionStorage = () => {
  const theme = localStorage.getItem("theme");
  localStorage.clear();
  if (theme) localStorage.setItem("theme", theme);
  sessionStorage.clear();
};

//
// Joseph 2026-07-29: "solve it permanently".
const setupApiErrorHandling = (logout: () => void) => {
  const originalFetch = window.fetch;

  if (!(window as any).__fetchIntercepted) {
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      if (response.status === 401) {
        // Only kill the session if OUR platform login token is
        // genuinely invalid — either missing, malformed, or expired.
        const token = localStorage.getItem("authToken");
        let ourTokenIsBad = false;
        if (!token) {
          ourTokenIsBad = true;
        } else {
          try {
            const decoded: DecodedToken = jwtDecode(token);
            // Missing or past-expiry exp → treat as invalid.
            ourTokenIsBad =
              !decoded?.exp || decoded.exp * 1000 <= Date.now();
          } catch {
            ourTokenIsBad = true;
          }
        }

        if (ourTokenIsBad) {
          logout();
          throw new Error("Authentication failed");
        }
        // Our token is still valid — the 401 came from some other
        // service. Let the caller handle it (toast, retry, ignore)
        // instead of nuking the whole session.
      }

      return response;
    };

    (window as any).__fetchIntercepted = true;
  }
};

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isReady, setIsReady] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const logout = () => {
    clearSessionStorage();
    setShouldRender(false);
    navigate("/login", { replace: true });
  };

  const checkAuthSync = () => {
    const token = localStorage.getItem("authToken");

    if (!token) return false;

    try {
      const decoded: DecodedToken = jwtDecode(token);
      return decoded.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  };

  useEffect(() => {
    setupApiErrorHandling(logout);

    const currentPath = location.pathname;
    const token = localStorage.getItem("authToken");

    //  No token
    if (!token) {
      if (currentPath !== "/login") {
        navigate("/login", { replace: true });
      }
      setShouldRender(currentPath === "/login");
      setIsReady(true);
      return;
    }

    //  Token exists
    try {
      const decoded: DecodedToken = jwtDecode(token);
      const isExpired = decoded.exp * 1000 < Date.now();

      if (isExpired) {
        clearSessionStorage();

        if (currentPath !== "/login") {
          navigate("/login", { replace: true });
        }

        setShouldRender(currentPath === "/login");
      } else {
        //  Block login page if already authenticated
        if (currentPath === "/login") {
          navigate("/dashboard", { replace: true });
          setShouldRender(false);
        } else if (currentPath === "/") {
          navigate("/dashboard", { replace: true });
          setShouldRender(false);
        } else {
          setShouldRender(true);
        }
      }
    } catch (error) {
      clearSessionStorage();

      if (currentPath !== "/login") {
        navigate("/login", { replace: true });
      }

      setShouldRender(currentPath === "/login");
    } finally {
      setIsReady(true);
    }
  }, [location.pathname, navigate]);

  // Auto logout when token expires
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    try {
      const decoded: DecodedToken = jwtDecode(token);
      const timeUntilExpiry = decoded.exp * 1000 - Date.now();

      if (timeUntilExpiry > 0) {
        timeoutId = setTimeout(() => {
          logout();
        }, timeUntilExpiry);
      } else {
        logout();
      }
    } catch {
      logout();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [location.pathname]);

  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const isValid = checkAuthSync();
      const currentPath = window.location.pathname;

      if (!isValid && currentPath !== "/login") {
        navigate("/login", { replace: true });
      }

      if (isValid && currentPath === "/login") {
        navigate("/dashboard", { replace: true });
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  if (!isReady) return null;

  return shouldRender ? <>{children}</> : null;
};

export default AuthGuard;